import { AlertCircle, Sparkles, TimerReset } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * TrialBanner — shows the broker's hybrid-trial status:
 *   - variant="active": still trialing (X days · N matches left)
 *   - variant="expired": trial expired, gated (upgrade CTA)
 * Pro brokers see nothing.
 */
export function TrialBanner({ trial, onUpgrade }) {
  if (!trial) return null;
  const { plan, expired, trial_days_left, trial_matches_left } = trial;
  if (plan === "pro" || plan === "admin") return null;

  if (expired) {
    return (
      <div
        className="mx-auto mt-4 flex w-full max-w-7xl items-start gap-3 rounded-2xl border border-brand-navy/25 bg-white px-5 py-4 shadow-mb-card animate-mb-fade-in"
        data-testid="trial-banner-expired"
      >
        <span className="mt-0.5 flex h-9 w-9 flex-none items-center justify-center rounded-full bg-brand-navy/10 text-brand-navy">
          <AlertCircle className="h-4 w-4" />
        </span>
        <div className="flex-1">
          <div className="text-sm font-semibold text-gray-900">
            Your free trial has ended
          </div>
          <p className="mt-0.5 text-xs text-gray-500">
            Pick a paid tier to keep adding listings, requirements, and
            advancing deals. Your existing data is safe — read access stays
            open.
          </p>
        </div>
        <Button
          type="button"
          onClick={onUpgrade}
          className="mb-focus-ring h-9 rounded-xl bg-brand-navy px-4 text-xs font-semibold text-white hover:bg-brand-navy-hover"
          data-testid="trial-banner-upgrade"
        >
          Choose a plan
        </Button>
      </div>
    );
  }

  return (
    <div
      className="mx-auto mt-4 flex w-full max-w-7xl items-start gap-3 rounded-2xl border border-brand-green/20 bg-brand-green/5 px-5 py-3 animate-mb-fade-in"
      data-testid="trial-banner-active"
    >
      <span className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-full bg-brand-green/15 text-brand-green">
        <TimerReset className="h-4 w-4" />
      </span>
      <div className="flex-1 text-xs">
        <div className="text-sm font-semibold text-gray-900">
          You&apos;re on the free trial
        </div>
        <div className="mt-0.5 text-gray-500">
          {trial_days_left} day{trial_days_left === 1 ? "" : "s"} left ·{" "}
          {trial_matches_left} match{trial_matches_left === 1 ? "" : "es"} left
          on the 3-match cap.
        </div>
      </div>
      <button
        type="button"
        onClick={onUpgrade}
        className="mb-focus-ring inline-flex h-8 items-center gap-1.5 rounded-lg border border-brand-green/40 bg-white px-3 text-xs font-semibold text-brand-green hover:bg-brand-green/10"
        data-testid="trial-banner-upgrade"
      >
        <Sparkles className="h-3 w-3" />
        Upgrade
      </button>
    </div>
  );
}
